import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useWebSocket } from '../hooks/useWebSocket'

const SEV = {
  low:      { bg: 'var(--success-light)', color: 'var(--success-text)', label: 'Bajo' },
  medium:   { bg: 'var(--warning-light)', color: 'var(--warning-text)', label: 'Medio' },
  high:     { bg: 'var(--danger-light)',  color: 'var(--danger-text)',  label: 'Alto' },
  critical: { bg: '#F3E8FF',             color: '#6B21A8',             label: 'Crítico' },
}
const STATUS_LABELS = {
  open: 'Abierto', acknowledged: 'Visto', in_progress: 'En curso',
  resolved: 'Resuelto', closed: 'Cerrado',
}
const STATUS_NEXT = {
  open: 'acknowledged', acknowledged: 'in_progress',
  in_progress: 'resolved', resolved: 'closed',
}
const STATUS_BTN = {
  open: 'Marcar como visto', acknowledged: 'Iniciar atención',
  in_progress: 'Resolver', resolved: 'Cerrar',
}

// ─── TimelineEvent ────────────────────────────────────────────────────────────

function TimelineEvent({ ev }) {
  const fmtTime = iso => {
    if (!iso) return ''
    const d = new Date(iso)
    return `${d.getDate()}/${d.getMonth()+1} ${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}`
  }
  const colors = {
    created: 'var(--accent)', status_change: 'var(--warning)',
    note: 'var(--text-muted)', snapshot: 'var(--success)', assignment: 'var(--info)',
  }
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 5,
        background: colors[ev.type] || 'var(--text-muted)',
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{ev.description}</div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{fmtTime(ev.created_at)}</div>
      </div>
    </div>
  )
}

// ─── EvidencePanel ────────────────────────────────────────────────────────────

const EV_TYPE_ICON = { image: '🖼', video: '🎥', document: '📄', audio: '🔊', other: '📎' }
const EV_TYPE_ACCEPT = {
  image: 'image/*',
  video: 'video/*',
  document: '.pdf,.doc,.docx,.txt',
  audio: 'audio/*',
  other: '*',
}

function EvidencePanel({ incidentId, token }) {
  const [items,    setItems]    = useState([])
  const [loading,  setLoading]  = useState(true)
  const [uploading,setUploading]= useState(false)
  const [modal,    setModal]    = useState(null)  // null | 'upload' | custody-item
  const [desc,     setDesc]     = useState('')
  const [camId,    setCamId]    = useState('')
  const [file,     setFile]     = useState(null)
  const [custody,  setCustody]  = useState(null)
  const [error,    setError]    = useState('')
  const fileRef = useRef()

  const headers = { Authorization: `Bearer ${token}` }

  const load = async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/evidence/${incidentId}/`, { headers })
      if (r.ok) setItems(await r.json())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [incidentId])

  const openCustody = async (ev) => {
    setCustody(null)
    setModal({ type: 'custody', ev })
    const r = await fetch(`/api/evidence/${incidentId}/${ev.id}/custody`, { headers })
    if (r.ok) setCustody(await r.json())
  }

  const upload = async () => {
    if (!file) { setError('Selecciona un archivo'); return }
    setUploading(true); setError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (desc.trim()) fd.append('description', desc.trim())
      if (camId.trim()) fd.append('camera_id', camId.trim())

      const r = await fetch(`/api/evidence/${incidentId}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        throw new Error(e.detail || `Error ${r.status}`)
      }
      setModal(null)
      setFile(null); setDesc(''); setCamId('')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  const seal = async (ev) => {
    if (!confirm(`¿Sellar la evidencia "${ev.original_name}"?\nUna vez sellada no puede modificarse.`)) return
    const r = await fetch(`/api/evidence/${incidentId}/${ev.id}/seal`, {
      method: 'POST', headers,
    })
    if (r.ok) await load()
  }

  const fmtSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes/1024).toFixed(1)} KB`
    return `${(bytes/1024/1024).toFixed(1)} MB`
  }

  const fmtTs = (ts) => ts ? new Date(ts).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' }) : '—'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '0 0 12px',
        borderBottom: '1px solid var(--border)', marginBottom: 12,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Evidencias ({items.length})
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button
            onClick={() => window.open(`/api/evidence/${incidentId}/report?token=${token}`, '_blank')}
            style={{
              padding: '4px 10px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer',
            }}
          >
            ⛓ Cadena custodia
          </button>
          <button
            onClick={() => { setModal({ type: 'upload' }); setError('') }}
            style={{
              padding: '4px 10px', borderRadius: 'var(--radius-sm)',
              border: 'none', background: 'var(--accent)', color: '#fff',
              fontSize: 11, cursor: 'pointer', fontWeight: 500,
            }}
          >
            + Agregar
          </button>
        </div>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 11 }}>
            Cargando…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
            Sin evidencias registradas
          </div>
        )}

        {items.map(ev => (
          <div key={ev.id} style={{
            border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8,
            overflow: 'hidden',
          }}>
            {/* Header de la evidencia */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', background: 'var(--bg-muted)',
              borderBottom: '1px solid var(--border)',
            }}>
              <span style={{ fontSize: 16 }}>{EV_TYPE_ICON[ev.evidence_type] || '📎'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ev.original_name}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {fmtSize(ev.size_bytes)} · {ev.mime_type}
                </div>
              </div>
              {ev.sealed && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 6px',
                  background: '#F3E8FF', color: '#6B21A8', borderRadius: 6,
                  flexShrink: 0,
                }}>
                  🔒 Sellada
                </span>
              )}
            </div>

            {/* Detalles */}
            <div style={{ padding: '8px 12px' }}>
              {ev.description && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  {ev.description}
                </div>
              )}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: 6 }}>
                SHA-256: {ev.sha256}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>
                Subido: {fmtTs(ev.uploaded_at)}
                {ev.camera_id && ` · Cámara: ${ev.camera_id}`}
              </div>

              {/* Acciones */}
              <div style={{ display: 'flex', gap: 6 }}>
                <a
                  href={`/api/evidence/${incidentId}/${ev.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'transparent',
                    color: 'var(--text-primary)', fontSize: 10, cursor: 'pointer',
                    textDecoration: 'none', display: 'inline-block',
                  }}
                >
                  ⬇ Descargar
                </a>
                <button
                  onClick={() => openCustody(ev)}
                  style={{
                    padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'transparent',
                    color: 'var(--text-muted)', fontSize: 10, cursor: 'pointer',
                  }}
                >
                  ⛓ Custodia
                </button>
                {!ev.sealed && (
                  <button
                    onClick={() => seal(ev)}
                    style={{
                      padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                      border: '1px solid #c4b5fd', background: 'transparent',
                      color: '#6B21A8', fontSize: 10, cursor: 'pointer',
                    }}
                  >
                    🔒 Sellar
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Modal upload ───────────────────────────────────────────────── */}
      {modal?.type === 'upload' && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setModal(null)}
        >
          <div
            style={{
              background: 'var(--bg-surface)', borderRadius: 12,
              border: '1px solid var(--border)', width: 440,
              padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
              📎 Agregar evidencia
            </div>

            {/* Drop zone */}
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 8, padding: '20px 16px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 12, transition: 'border-color 0.15s',
                background: file ? 'var(--bg-muted)' : 'var(--bg-root)',
              }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f) }}
            >
              {file ? (
                <div>
                  <div style={{ fontSize: 14 }}>{EV_TYPE_ICON[file.type.split('/')[0]] || '📎'}</div>
                  <div style={{ fontWeight: 600, fontSize: 12, marginTop: 4 }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>⬆️</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Arrastra un archivo o haz clic para seleccionar
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                    Imágenes, videos, PDFs, documentos (máx. 200 MB)
                  </div>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                style={{ display: 'none' }}
                onChange={e => setFile(e.target.files[0] || null)}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>
                Descripción
              </div>
              <textarea
                value={desc}
                onChange={e => setDesc(e.target.value)}
                rows={2}
                placeholder="Descripción de la evidencia…"
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'var(--bg-root)',
                  color: 'var(--text-primary)', fontSize: 12, resize: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>
                ID de cámara (opcional)
              </div>
              <input
                value={camId}
                onChange={e => setCamId(e.target.value)}
                placeholder="cam-001"
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'var(--bg-root)',
                  color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '7px 10px', borderRadius: 6, marginBottom: 12,
                background: '#fef2f2', border: '1px solid #fecaca',
                color: '#dc2626', fontSize: 11,
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setModal(null)}
                style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={upload}
                disabled={uploading || !file}
                style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--accent)', color: '#fff',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  opacity: uploading || !file ? 0.6 : 1,
                }}
              >
                {uploading ? 'Subiendo…' : '⬆️ Subir evidencia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal cadena de custodia ────────────────────────────────────── */}
      {modal?.type === 'custody' && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => { setModal(null); setCustody(null) }}
        >
          <div
            style={{
              background: 'var(--bg-surface)', borderRadius: 12,
              border: '1px solid var(--border)', width: 600,
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>⛓</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Cadena de custodia</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {modal.ev?.original_name}
                </div>
              </div>
              <button
                onClick={() => { setModal(null); setCustody(null) }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
              >×</button>
            </div>

            {/* Hash */}
            {modal.ev && (
              <div style={{
                padding: '8px 20px', background: 'var(--bg-muted)',
                borderBottom: '1px solid var(--border)',
                fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all',
                color: 'var(--text-secondary)',
              }}>
                SHA-256: {modal.ev.sha256}
              </div>
            )}

            {/* Logs */}
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              {!custody && (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>
                  Cargando registros…
                </div>
              )}
              {custody?.custody_chain?.length === 0 && (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>
                  Sin registros en la cadena
                </div>
              )}
              {custody?.custody_chain?.map((c, i) => {
                const ACTION_LABELS = {
                  uploaded: { label: 'Carga inicial', icon: '📤', color: '#059669' },
                  viewed:   { label: 'Visualización', icon: '👁',  color: '#3b82f6' },
                  downloaded: { label: 'Descarga',    icon: '⬇',  color: '#8b5cf6' },
                  sealed:   { label: 'Sellado',       icon: '🔒', color: '#6B21A8' },
                  exported: { label: 'Exportación',   icon: '📋', color: '#0ea5e9' },
                  deleted:  { label: 'Eliminación',   icon: '🗑', color: '#dc2626' },
                }
                const meta = ACTION_LABELS[c.action] || { label: c.action, icon: '•', color: 'var(--text-muted)' }
                return (
                  <div key={c.id} style={{
                    display: 'flex', gap: 10, padding: '8px 0',
                    borderBottom: '1px solid var(--border)', fontSize: 11,
                  }}>
                    <div style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{meta.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                        <span style={{ fontWeight: 700, color: meta.color }}>{meta.label}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                          {c.actor_name || 'Sistema'} · {c.actor_role || '—'}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-muted)' }}>
                        {new Date(c.timestamp).toLocaleString('es-CL')}
                        {c.ip_address && ` · IP: ${c.ip_address}`}
                      </div>
                      {c.notes && (
                        <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{c.notes}</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── IncidentView (principal) ─────────────────────────────────────────────────

export default function IncidentView() {
  const { id }    = useParams()
  const token     = useAuthStore(s => s.token)
  const [inc, setInc]     = useState(null)
  const [note, setNote]   = useState('')
  const [saving, setSaving] = useState(false)
  const [leftTab, setLeftTab] = useState('timeline')  // 'timeline' | 'evidence'

  const load = async () => {
    if (!token || !id) return
    const res = await fetch(`/api/incidents/${id}`, { headers: { Authorization: `Bearer ${token}` } })
    if (res.ok) setInc(await res.json())
  }

  useEffect(() => { load() }, [id, token])

  useWebSocket((msg) => {
    if (msg.type === 'incident_updated' && msg.incident?.id === Number(id)) {
      setInc(prev => prev ? { ...prev, ...msg.incident } : prev)
    }
    if (msg.type === 'incident_event' && msg.incident_id === Number(id)) {
      setInc(prev => prev ? { ...prev, events: [...(prev.events || []), msg.event] } : prev)
    }
  })

  const updateStatus = async () => {
    if (!inc) return
    const next = STATUS_NEXT[inc.status]
    if (!next) return
    const res = await fetch(`/api/incidents/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next }),
    })
    if (res.ok) setInc(prev => ({ ...prev, status: next }))
  }

  const addNote = async () => {
    if (!note.trim()) return
    setSaving(true)
    const res = await fetch(`/api/incidents/${id}/events`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'note', description: note }),
    })
    if (res.ok) { setNote(''); await load() }
    setSaving(false)
  }

  if (!inc) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-root)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cargando incidente…</span>
    </div>
  )

  const sev = SEV[inc.severity] || SEV.medium

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-root)', fontFamily: 'var(--font)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)',
        padding: '12px 20px', display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              background: sev.bg, color: sev.color,
            }}>{sev.label}</span>
            <span style={{
              fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
              border: '1px solid var(--border)', color: 'var(--text-secondary)',
            }}>{STATUS_LABELS[inc.status] || inc.status}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>#{inc.id}</span>
          </div>
          <h1 style={{ fontSize: 16, fontWeight: 600 }}>{inc.title}</h1>
          {inc.description && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{inc.description}</p>
          )}
        </div>

        {STATUS_NEXT[inc.status] && (
          <button
            onClick={updateStatus}
            style={{
              padding: '8px 16px', background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius-sm)',
              fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0,
            }}
          >
            {STATUS_BTN[inc.status]}
          </button>
        )}
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 0, overflow: 'hidden' }}>

        {/* Panel izquierdo con tabs */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid var(--border)' }}>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: 0, borderBottom: '1px solid var(--border)',
            padding: '0 20px', flexShrink: 0,
          }}>
            {[['timeline','📋 Línea de tiempo'], ['evidence','🔒 Evidencias']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setLeftTab(id)}
                style={{
                  padding: '10px 14px', border: 'none', background: 'transparent',
                  fontSize: 12, fontWeight: leftTab === id ? 600 : 400, cursor: 'pointer',
                  color: leftTab === id ? 'var(--accent)' : 'var(--text-muted)',
                  borderBottom: leftTab === id ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Timeline */}
          {leftTab === 'timeline' && (
            <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
              {(inc.events || []).map(ev => <TimelineEvent key={ev.id} ev={ev} />)}
              {(inc.events || []).length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>
                  Sin eventos registrados aún
                </div>
              )}
              <div style={{ marginTop: 16 }}>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Agregar nota al incidente…"
                  rows={3}
                  style={{
                    width: '100%', padding: '8px 10px', fontSize: 12,
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg-surface)', color: 'var(--text-primary)',
                    resize: 'vertical', outline: 'none', fontFamily: 'var(--font)',
                  }}
                />
                <button
                  onClick={addNote} disabled={saving || !note.trim()}
                  style={{
                    marginTop: 6, padding: '6px 14px', background: 'var(--accent)', color: '#fff',
                    border: 'none', borderRadius: 'var(--radius-sm)',
                    fontSize: 12, cursor: 'pointer', opacity: saving || !note.trim() ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Guardando…' : 'Agregar nota'}
                </button>
              </div>
            </div>
          )}

          {/* Evidencia */}
          {leftTab === 'evidence' && (
            <div style={{ flex: 1, overflow: 'hidden', padding: 20, display: 'flex', flexDirection: 'column' }}>
              <EvidencePanel incidentId={id} token={token} />
            </div>
          )}
        </div>

        {/* Sidebar info */}
        <div style={{ padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {inc.snapshot_b64 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Captura IA</div>
              <img
                src={`data:image/jpeg;base64,${inc.snapshot_b64}`}
                alt="Snapshot"
                style={{ width: '100%', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
              />
            </div>
          )}

          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Detalle</div>
            {[
              ['ID',          `#${inc.id}`],
              ['Sitio',       inc.site_id ? `Sitio #${inc.site_id}` : '—'],
              ['Severidad',   sev.label],
              ['Estado',      STATUS_LABELS[inc.status] || inc.status],
              ['Creado',      inc.created_at ? new Date(inc.created_at).toLocaleString('es-CL') : '—'],
              ['Resuelto',    inc.resolved_at ? new Date(inc.resolved_at).toLocaleString('es-CL') : '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{val}</span>
              </div>
            ))}
          </div>

          {inc.scene_description && (
            <div style={{ background: 'var(--info-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--info-text)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Análisis IA</div>
              <p style={{ fontSize: 12, color: 'var(--info-text)' }}>{inc.scene_description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
